
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Logo />
            <Link 
              to="/" 
              className="flex items-center text-gray-600 hover:text-green-600"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Last updated: January 1, 2025</p>

          <div className="space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Our Commitment to Your Privacy</h2>
              <p className="text-gray-700">
                At Sage Saver (sagesaver.net), we are committed to protecting your privacy and personal information. 
                We will never sell, rent, or share your personal data with third parties for marketing purposes. 
                This Privacy Policy explains how we collect, use, and protect your information.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Information We Collect</h2>
              <p className="text-gray-700 mb-3">We collect the following information when you sign up for our services:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>First and last name</li>
                <li>Email address</li>
                <li>Phone number (optional)</li>
                <li>Profession/occupation (optional)</li>
                <li>How you heard about us (optional)</li>
                <li>Communication preferences</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">How We Use Your Information</h2>
              <p className="text-gray-700 mb-3">We use your information to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Send you information about assistance programs and financial resources</li>
                <li>Provide discounts and coupons relevant to your needs</li>
                <li>Send financial planning tips and educational content</li>
                <li>Improve our services and website functionality</li>
                <li>Comply with legal requirements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Email and Text Communications</h2>
              <p className="text-gray-700 mb-3">
                We only send emails and text messages to users who have explicitly opted in to receive them. 
                All our communications comply with the CAN-SPAM Act:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>We clearly identify ourselves as the sender</li>
                <li>Subject lines accurately reflect the email content</li>
                <li>Every email includes an easy unsubscribe option</li>
                <li>We honor unsubscribe requests within 10 business days</li>
                <li>We include our physical address in all emails</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Data Security</h2>
              <p className="text-gray-700">
                We implement appropriate technical and organizational measures to protect your personal information 
                against unauthorized access, alteration, disclosure, or destruction. Your data is stored on secure 
                servers with encryption and access controls.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">We Never Sell Your Data</h2>
              <p className="text-gray-700">
                We want to be absolutely clear: Sage Saver will never sell, rent, lease, or otherwise share your 
                personal information with third parties for their marketing purposes. Your trust is essential to 
                our mission of helping families access assistance and savings.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Your Rights</h2>
              <p className="text-gray-700 mb-3">You have the right to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Access the personal information we have about you</li>
                <li>Correct inaccurate or incomplete information</li>
                <li>Delete your personal information</li>
                <li>Unsubscribe from emails at any time</li>
                <li>Opt out of text messages by texting STOP</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Contact Us</h2>
              <p className="text-gray-700">
                If you have questions about this Privacy Policy or want to exercise your rights, contact us at:
              </p>
              <div className="mt-3 text-gray-700">
                <p>Email: support@sagesaver.net</p>
                <p>Website: sagesaver.net</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Changes to This Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time. We will notify you of any changes by 
                posting the new policy on this page and updating the "Last updated" date.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
